(function () {
  const config = window.moducubeAnalytics || {};

  if (!config.enabled) return;

  const debugEnabled = new URLSearchParams(window.location.search).get('debug_moducube_analytics') === '1';
  const scrollThresholds = [25, 50, 75, 90, 100];
  const firedScrollDepths = new Set();
  const viewedSections = new Set();
  const viewedMedia = new Set();
  const debugEvents = [];

  window.dataLayer = window.dataLayer || [];

  function getVariantId() {
    const selectedVariantInput = document.querySelector('form[action*="/cart/add"] [name="id"], product-form [name="id"], .ecom-product-form [name="id"]');
    return config.variant_id || selectedVariantInput?.value || null;
  }

  function cleanText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
  }

  function getSectionName(element) {
    const section = element?.closest?.('[data-ga4-section], .shopify-section, .ecom-section, section');
    if (!section) return null;

    if (section.dataset.ga4Section) return section.dataset.ga4Section;
    if (section.id) return section.id;
    if (section.dataset.id) return section.dataset.id;

    return null;
  }

  function getElementLabel(element) {
    if (!element) return null;

    return (
      element.dataset.ga4Label ||
      element.getAttribute('aria-label') ||
      element.getAttribute('title') ||
      element.getAttribute('href') ||
      cleanText(element.textContent)
    );
  }

  function basePayload(extra) {
    return {
      page_name: config.page_name || 'moducube_3_seater_plus_ottoman',
      page_type: config.page_type || 'product_landing',
      product_handle: config.product_handle || null,
      product_id: config.product_id || null,
      variant_id: getVariantId(),
      section: extra.section || null,
      interaction: extra.interaction || null,
      label: extra.label || null,
      ...extra,
    };
  }

  function pushEvent(eventName, payload) {
    const eventPayload = {
      event: eventName,
      ...basePayload(payload || {}),
    };

    window.dataLayer.push(eventPayload);

    if (debugEnabled) {
      debugEvents.push(eventPayload);

      let debugOutput = document.getElementById('moducube-analytics-debug-events');
      if (!debugOutput) {
        debugOutput = document.createElement('script');
        debugOutput.id = 'moducube-analytics-debug-events';
        debugOutput.type = 'application/json';
        document.head.appendChild(debugOutput);
      }

      debugOutput.textContent = JSON.stringify(debugEvents);
      document.documentElement.dataset.moducubeAnalyticsEventCount = String(debugEvents.length);
      console.info(`[Modu cube analytics] ${JSON.stringify(eventPayload)}`);
    }
  }

  function inferInteraction(target) {
    if (!target) return null;

    const explicit = target.closest('[data-ga4-interaction]');
    if (explicit) return explicit.dataset.ga4Interaction;

    if (target.closest('.ecom-product-single__variant-picker, variant-selects')) return 'variant_click';
    if (target.closest('.ecom-product-single__media, media-gallery, product-modal')) return 'media_click';

    const actionElement = target.closest('a, button, summary, [role="button"], input[type="radio"], input[type="checkbox"]');
    if (!actionElement) return null;

    const label = cleanText(getElementLabel(actionElement)).toLowerCase();
    const href = actionElement.getAttribute('href') || '';
    const isPrimaryCta = /shop|buy|add|cart|checkout|order|get|select/.test(label) || /\/cart|\/checkout|#/.test(href);

    if (actionElement.tagName === 'SUMMARY') return 'faq_open';
    if (isPrimaryCta) return 'primary_cta';

    return null;
  }

  function trackClicks() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trackedElement = target?.closest?.('[data-ga4-interaction], a, button, summary, [role="button"], input[type="radio"], input[type="checkbox"]');
        if (!trackedElement) return;

        const interaction = inferInteraction(trackedElement);
        if (!interaction) return;

        if (interaction === 'faq_open') {
          const details = trackedElement.closest('details');
          if (details?.open) return;
        }

        const faqGroup = interaction === 'faq_open' ? trackedElement.closest('[data-ga4-faq-category]') : null;

        pushEvent('moducube_interaction', {
          section: getSectionName(trackedElement),
          interaction,
          label: getElementLabel(trackedElement),
          ...(interaction === 'faq_open'
            ? {
                faq_question: cleanText(trackedElement.textContent),
                faq_category: faqGroup?.dataset.ga4FaqCategory || null,
              }
            : {}),
        });
      },
      true
    );
  }

  function trackProductFormSubmits() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target instanceof HTMLFormElement ? event.target : null;
        if (!form) return;

        const isProductForm = form.matches('form[action*="/cart/add"], .ecom-product-form') || form.closest('product-form');
        if (!isProductForm) return;

        pushEvent('moducube_interaction', {
          section: getSectionName(form),
          interaction: 'product_form_submit',
          label: form.id || 'add_to_cart_form',
        });
      },
      true
    );
  }

  function getTrackedSections() {
    const explicitSections = Array.from(document.querySelectorAll('[data-ga4-section]'));
    if (explicitSections.length > 0) return explicitSections;

    return Array.from(document.querySelectorAll('#MainContent .ecom-section, #MainContent .shopify-section, #MainContent section'));
  }

  function trackSectionViews() {
    const sections = getTrackedSections();
    if (sections.length === 0) return;

    let ticking = false;

    const checkSections = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      sections.forEach((section, index) => {
        const name = getSectionName(section) || `section_${index + 1}`;
        if (viewedSections.has(name)) return;

        const rect = section.getBoundingClientRect();
        if (rect.height <= 0 || viewportHeight <= 0) return;

        const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
        const requiredVisibleHeight = Math.min(rect.height * 0.5, viewportHeight * 0.5);
        if (visibleHeight < requiredVisibleHeight) return;

        viewedSections.add(name);
        pushEvent('moducube_section_view', {
          section: name,
          label: section.dataset.ga4Label || name,
        });
      });
    };

    const requestCheck = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        checkSections();
        ticking = false;
      });
    };

    window.addEventListener('scroll', requestCheck, { passive: true });
    window.addEventListener('resize', requestCheck);
    requestCheck();
  }

  function trackMediaViews() {
    const mediaElements = Array.from(document.querySelectorAll('[data-ga4-media-view]'));
    if (mediaElements.length === 0) return;

    const recordMediaView = (element, index) => {
      const label = element.dataset.ga4MediaId || element.dataset.ga4Label || `media_${index + 1}`;
      if (viewedMedia.has(label)) return;

      viewedMedia.add(label);
      pushEvent('moducube_interaction', {
        section: getSectionName(element),
        interaction: 'media_view',
        label,
        media_type: element.dataset.ga4MediaType || 'image',
      });
    };

    if (!('IntersectionObserver' in window)) {
      mediaElements.forEach((element, index) => recordMediaView(element, index));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;

          const index = mediaElements.indexOf(entry.target);
          recordMediaView(entry.target, index);
          observer.unobserve(entry.target);
        });
      },
      { threshold: [0.6] }
    );

    mediaElements.forEach((element) => observer.observe(element));
  }

  function getScrollDepth() {
    const doc = document.documentElement;
    const body = document.body;
    const scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
    const scrollHeight = Math.max(body.scrollHeight, doc.scrollHeight, body.offsetHeight, doc.offsetHeight);
    const viewportHeight = window.innerHeight || doc.clientHeight || 0;
    const scrollableDistance = scrollHeight - viewportHeight;

    if (scrollableDistance <= 0) return 100;

    return Math.min(100, Math.round(((scrollTop + viewportHeight) / scrollHeight) * 100));
  }

  function trackScrollDepth() {
    const currentDepth = getScrollDepth();

    scrollThresholds.forEach((threshold) => {
      if (currentDepth < threshold || firedScrollDepths.has(threshold)) return;

      firedScrollDepths.add(threshold);
      pushEvent('moducube_scroll_depth', {
        section: 'page',
        interaction: 'scroll',
        label: `${threshold}%`,
        scroll_depth: threshold,
      });
    });
  }

  function initScrollTracking() {
    let ticking = false;

    const requestCheck = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        trackScrollDepth();
        ticking = false;
      });
    };

    window.addEventListener('scroll', requestCheck, { passive: true });
    window.addEventListener('resize', requestCheck);
    requestCheck();
  }

  function init() {
    trackClicks();
    trackProductFormSubmits();
    trackSectionViews();
    trackMediaViews();
    initScrollTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
